/**
 * VolumeBus — pure volume math utilities for the AudioManager.
 *
 * Both functions are stateless and NullEngine-safe (no Babylon imports).
 * They are the single source of truth for:
 *   - computing a channel's effective volume as a function of master + channel + base
 *   - computing a ducked music volume during cinematic moments
 */

/**
 * Compute the effective volume for a single sound.
 *
 * @param master  Master volume slider [0..1].
 * @param channel Channel volume slider (sfx, ambient, music) [0..1].
 * @param base    Per-sound baseline ratio (default 1.0 — e.g. purrLoop uses 0.6 of sfxVolume).
 * @returns       Product of all three, clamped to [0..1].
 *
 * @example
 * // Purr loop: sfxVolume=0.5, master=1.0, purr ratio=0.6 → 0.30
 * effectiveVolume(1.0, 0.5, 0.6); // 0.30
 */
export function effectiveVolume(master: number, channel: number, base = 1.0): number {
  return Math.max(0, Math.min(1, master * channel * base));
}

/**
 * Compute the music volume, optionally ducked for cinematic moments.
 *
 * @param musicVol  Current music bus volume [0..1].
 * @param ducking   True when music should be ducked (e.g. ELDER_ENCOUNTER, LEVEL_COMPLETE).
 * @param factor    Duck factor (default 0.3 — music drops to 30% during cinematic moments).
 * @returns         Effective music volume, clamped to [0..1].
 */
export function duckedMusic(musicVol: number, ducking: boolean, factor = 0.3): number {
  const vol = ducking ? musicVol * factor : musicVol;
  return Math.max(0, Math.min(1, vol));
}
